import React, {
	useEffect,
	useState,
} from 'react';
import { Card } from 'antd';
import {
	BankAccountTypes,
	CustomFieldParentTypes,
} from '@shared/utils/constants';
import type { BankAccountProps } from '@api/models/bank-account';
import type { FixingCompanyProps } from '@api/models/fixing-company';
import type { UpdateBankAccountRequest } from '@api/features/bank-accounts/updateBankAccount';
import type { BankAccountWithCurrencyFlag } from '@api/features/vessels/getVesselDetails';
import { BankAccountTabs } from '@client/screens/fleet/VesselDetailsScreen/components/BankAccountTabs';
import {
	createBankAccount,
	deleteBankAccount,
	updateBankAccount,
} from '@client/lib/api';
import CustomFields from './CustomFields/CustomFields';

type Props = {
	fixingCompany: FixingCompanyProps;
	fixingCompanies: Array<FixingCompanyProps>;
	bankAccounts: Array<BankAccountWithCurrencyFlag>;
	refreshBankAccounts: () => Promise<unknown> | void;
};

const FixingCompanyBankAccounts = ({
	fixingCompany,
	fixingCompanies,
	bankAccounts,
	refreshBankAccounts,
}: Props) => {
	const [activeBankAccountTab, setActiveBankAccountTab] = useState<number | undefined>();

	useEffect(() => {
		if (!activeBankAccountTab || !bankAccounts.some((a) => a.id === activeBankAccountTab)) {
			setActiveBankAccountTab(bankAccounts[0]?.id);
		}
	}, [activeBankAccountTab, bankAccounts]);

	const handleSaveChanges = async (account: BankAccountProps, values: UpdateBankAccountRequest['attributes']) => {
		await updateBankAccount(values, account.id);
		await refreshBankAccounts();
	};

	const handleAddAccount = async () => {
		const newAccount = await createBankAccount(BankAccountTypes.OURS, undefined, fixingCompany.id);
		await refreshBankAccounts();

		return newAccount;
	};

	const handleDeleteAccount = async (id: number) => {
		await deleteBankAccount(id);
		await refreshBankAccounts();
	};

	return (
		<Card
			bordered
			title="Bank accounts"
		>
			<BankAccountTabs
				bankAccounts={bankAccounts}
				deleteBankAccount={handleDeleteAccount}
				addBankAccount={handleAddAccount}
				saveBankAccount={handleSaveChanges}
				activeBankAccountTab={activeBankAccountTab}
				setActiveBankAccountTab={setActiveBankAccountTab}
				allowCreate
				showInvoiceCompanyName
				fixingCompanies={fixingCompanies}
			/>
			{activeBankAccountTab && (
				<CustomFields
					parentId={activeBankAccountTab}
					parentType={CustomFieldParentTypes.BANK}
				/>
			)}
		</Card>
	);
};

export default FixingCompanyBankAccounts;
